<template>
    <DocsPageLayout
        title="Core / Live Events"
        subtitle="Public API guide for runtime event ingestion and Live Mode monitoring."
        :nav-links="navLinks"
        :nav-groups="navGroups"
        :toc-items="tocItems"
        active-nav-to="/docs/core/live-mode-events"
    >
        <Card id="intro" class="content-card introduction">
            <template #content>
                <p>This guide documents the Live Mode event APIs.</p>
                <p v-if="isLoadingOpenApi" class="openapi-status">
                    Loading endpoint details from the public Live Mode spec...
                </p>
                <p v-else-if="openApiError" class="openapi-status error">
                    Could not load the public Live Mode spec. Showing fallback
                    values.
                </p>
                <p v-else class="openapi-status">
                    Endpoint details below are loaded from
                    <code>{{ openApiUrl }}</code>
                    and update automatically when the published Live Mode spec
                    changes.
                </p>
            </template>
        </Card>

        <Card id="quick-start" class="content-card">
            <template #title>
                <h2>Quick Start</h2>
            </template>
            <template #content>
                <ul class="icon-list">
                    <li>
                        <i class="pi pi-check-circle" aria-hidden="true"></i>
                        <span>Create an API key in MyStekz.</span>
                    </li>
                    <li>
                        <i class="pi pi-check-circle" aria-hidden="true"></i>
                        <span>
                            Send events to
                            <code>{{ ingestPath }}</code>
                            .
                        </span>
                    </li>
                    <li>
                        <i class="pi pi-check-circle" aria-hidden="true"></i>
                        <span>
                            Fetch metrics via
                            <code>{{ metricsPath }}</code>
                            .
                        </span>
                    </li>
                    <li>
                        <i class="pi pi-check-circle" aria-hidden="true"></i>
                        <span>
                            Use clear
                            <code>event_type</code>
                            naming conventions.
                        </span>
                    </li>
                </ul>
            </template>
        </Card>

        <Card id="authentication" class="content-card">
            <template #title>
                <h2>Authentication</h2>
            </template>
            <template #content>
                <p>Include API key and context headers in every request.</p>
                <pre><code>Authorization: Bearer &lt;api_key&gt;
Organization: &lt;organization_id&gt;
Stek: &lt;stek_id&gt;
Content-Type: application/json</code></pre>
            </template>
        </Card>

        <Card id="endpoints" class="content-card">
            <template #title>
                <h2>Endpoints</h2>
            </template>
            <template #content>
                <table>
                    <thead>
                        <tr>
                            <th>Method</th>
                            <th>Endpoint</th>
                            <th>Purpose</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="endpoint in endpointsForDocs"
                            :key="`${endpoint.method}:${endpoint.path}`"
                        >
                            <td>{{ endpoint.method }}</td>
                            <td>
                                <code>{{ endpoint.path }}</code>
                            </td>
                            <td>{{ endpoint.summary }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </Card>

        <Card id="event-details" class="content-card">
            <template #title>
                <h2>Event Details (OpenAPI)</h2>
            </template>
            <template #content>
                <article
                    v-for="endpoint in endpointsForDocs"
                    :key="`details-${endpoint.method}-${endpoint.path}`"
                    class="endpoint-detail-card"
                >
                    <header class="endpoint-detail-header">
                        <span class="endpoint-method">{{
                            endpoint.method
                        }}</span>
                        <code class="endpoint-path">{{ endpoint.path }}</code>
                    </header>

                    <div
                        v-if="endpoint.description"
                        class="endpoint-description markdown-description"
                        v-html="renderMarkdown(endpoint.description)"
                    />

                    <div class="detail-sections">
                        <section class="detail-panel">
                            <p class="detail-heading">Request Fields</p>
                            <ul
                                v-if="endpoint.requestFields.length > 0"
                                class="field-list"
                            >
                                <li
                                    v-for="field in endpoint.requestFields"
                                    :key="`${endpoint.path}-${field.name}`"
                                >
                                    <div class="field-topline">
                                        <code class="field-name">{{
                                            field.name
                                        }}</code>
                                        <span class="field-type">{{
                                            field.type
                                        }}</span>
                                        <span
                                            v-if="field.required"
                                            class="field-required"
                                        >
                                            required
                                        </span>
                                    </div>
                                    <p
                                        v-if="field.description"
                                        class="field-desc"
                                    >
                                        {{ field.description }}
                                    </p>
                                </li>
                            </ul>
                            <p v-else class="field-empty">
                                No request body fields defined in OpenAPI.
                            </p>
                        </section>

                        <section class="detail-panel">
                            <p class="detail-heading">Responses</p>
                            <div class="response-codes">
                                <code
                                    v-for="code in endpoint.responseCodes"
                                    :key="`${endpoint.path}-${code}`"
                                    class="response-code"
                                >
                                    {{ code }}
                                </code>
                            </div>
                        </section>
                    </div>

                    <section class="detail-panel detail-panel-example">
                        <p class="detail-heading">Example</p>
                        <div class="copy-block">
                            <button
                                type="button"
                                class="copy-button"
                                @click="
                                    copyText(
                                        endpointCurlExample(endpoint),
                                        `${endpoint.method}:${endpoint.path}`
                                    )
                                "
                            >
                                {{
                                    copiedKey ===
                                    `${endpoint.method}:${endpoint.path}`
                                        ? "Copied"
                                        : "Copy curl"
                                }}
                            </button>
                            <pre><code>{{ endpointCurlExample(endpoint) }}</code></pre>
                        </div>
                    </section>
                </article>
            </template>
        </Card>

        <Card id="rules" class="content-card">
            <template #title>
                <h2>Event Design Rules</h2>
            </template>
            <template #content>
                <ul class="icon-list">
                    <li>
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            Use dot notation:
                            <code>entity.action</code>
                            , e.g.
                            <code>task.completed</code>
                            .
                        </span>
                    </li>
                    <li>
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            Event types containing
                            <code>error</code>
                            are counted as errors.
                        </span>
                    </li>
                    <li>
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            If using
                            <code>process_id</code>
                            or
                            <code>process_element_id</code>
                            , include
                            <code>process_instance_id</code>
                            .
                        </span>
                    </li>
                    <li>
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            Prefer stable identifiers in
                            <code>business_key</code>
                            .
                        </span>
                    </li>
                </ul>
            </template>
        </Card>
    </DocsPageLayout>
</template>

<script setup lang="ts">
import MarkdownIt from "markdown-it";
import Card from "primevue/card";
import { computed, onMounted, ref } from "vue";

import DocsPageLayout from "@/components/docs/DocsPageLayout.vue";

type OpenApiSchema = {
    $ref?: string;
    type?: string;
    title?: string;
    description?: string;
    example?: unknown;
    required?: string[];
    properties?: Record<string, OpenApiSchema>;
    items?: OpenApiSchema;
};

type OpenApiOperation = {
    summary?: string;
    description?: string;
    requestBody?: {
        content?: Record<
            string,
            {
                schema?: OpenApiSchema;
                example?: unknown;
                examples?: Record<string, { value?: unknown }>;
            }
        >;
    };
    responses?: Record<string, { description?: string }>;
};

type OpenApiSpec = {
    paths?: Record<string, Record<string, OpenApiOperation>>;
    components?: {
        schemas?: Record<string, OpenApiSchema>;
    };
};

type DocField = {
    name: string;
    type: string;
    description?: string;
    required: boolean;
};

type DocEndpoint = {
    method: string;
    path: string;
    summary: string;
    description: string;
    requestFields: DocField[];
    responseCodes: string[];
    requestExample?: unknown;
};

const apiBase = (
    (import.meta.env.VITE_API_URL as string | undefined) ?? ""
).replace(/\/$/, "");
const openApiUrl = apiBase
    ? `${apiBase}/openapi/public.json`
    : "/openapi/public.json";

const openApiSpec = ref<OpenApiSpec | null>(null);
const isLoadingOpenApi = ref(false);
const openApiError = ref("");
const copiedKey = ref<string | null>(null);
const markdown = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: true,
});

const renderMarkdown = (content: string) => markdown.render(content);

const navLinks = [{ label: "Welcome to Docs", to: "/docs" }];

const navGroups = [
    {
        label: "Core",
        open: true,
        items: [{ label: "Live Events", to: "/docs/core/live-mode-events" }],
    },
];

const tocItems = [
    { id: "intro", label: "Introduction" },
    { id: "quick-start", label: "Quick Start" },
    { id: "authentication", label: "Authentication" },
    { id: "endpoints", label: "Endpoints" },
    { id: "event-details", label: "Event Details" },
    { id: "rules", label: "Event Design Rules" },
];

const fallbackEndpoints: DocEndpoint[] = [
    {
        method: "POST",
        path: "/stek/live_mgt/ingest_event/start",
        summary: "Ingest a runtime event",
        description: "",
        requestFields: [],
        responseCodes: ["200"],
    },
    {
        method: "POST",
        path: "/stek/live_mgt/get_metrics/start",
        summary: "Get aggregated metrics",
        description: "",
        requestFields: [],
        responseCodes: ["200"],
    },
    {
        method: "POST",
        path: "/stek/live_mgt/get_releases/start",
        summary: "List releases with tracked events",
        description: "",
        requestFields: [],
        responseCodes: ["200"],
    },
    {
        method: "POST",
        path: "/stek/live_mgt/get_compliance_evidence/start",
        summary: "Retrieve compliance evidence records",
        description: "",
        requestFields: [],
        responseCodes: ["200"],
    },
    {
        method: "POST",
        path: "/stek/live_mgt/get_process_instances/start",
        summary: "List process instance runtime metrics",
        description: "",
        requestFields: [],
        responseCodes: ["200"],
    },
];

const resolveSchema = (schema?: OpenApiSchema): OpenApiSchema | undefined => {
    if (!schema) {
        return undefined;
    }

    if (!schema.$ref) {
        return schema;
    }

    const refName = schema.$ref.split("/").pop();
    if (!refName) {
        return schema;
    }

    return openApiSpec.value?.components?.schemas?.[refName] ?? schema;
};

const inferSchemaType = (schema?: OpenApiSchema): string => {
    const resolved = resolveSchema(schema);
    if (!resolved) {
        return "unknown";
    }

    if (resolved.type === "array") {
        return `array<${inferSchemaType(resolved.items)}>`;
    }

    if (resolved.type) {
        return resolved.type;
    }

    if (resolved.properties) {
        return "object";
    }

    return resolved.title ?? "object";
};

const extractFields = (schema?: OpenApiSchema): DocField[] => {
    const resolved = resolveSchema(schema);
    if (!resolved?.properties) {
        return [];
    }

    const required = new Set(resolved.required ?? []);

    return Object.entries(resolved.properties).map(([name, fieldSchema]) => {
        const fieldResolved = resolveSchema(fieldSchema);
        return {
            name,
            type: inferSchemaType(fieldResolved),
            description: fieldResolved?.description,
            required: required.has(name),
        };
    });
};

const extractRequestExample = (operation: OpenApiOperation): unknown => {
    const content = operation.requestBody?.content;
    const appJson = content?.["application/json"];

    if (appJson?.example !== undefined) {
        return appJson.example;
    }

    const firstExample = appJson?.examples
        ? Object.values(appJson.examples)[0]?.value
        : undefined;

    if (firstExample !== undefined) {
        return firstExample;
    }

    const schemaExample = resolveSchema(appJson?.schema)?.example;
    return schemaExample;
};

const endpointsFromOpenApi = computed<DocEndpoint[]>(() => {
    const paths = openApiSpec.value?.paths;
    if (!paths) {
        return [];
    }

    const endpoints: DocEndpoint[] = [];

    Object.entries(paths).forEach(([path, operations]) => {
        Object.entries(operations).forEach(([method, operation]) => {
            const normalizedMethod = method.toLowerCase();
            if (
                !["get", "post", "put", "patch", "delete"].includes(
                    normalizedMethod
                )
            ) {
                return;
            }

            const appJsonSchema =
                operation.requestBody?.content?.["application/json"]?.schema;

            endpoints.push({
                method: normalizedMethod.toUpperCase(),
                path,
                summary:
                    operation.summary ||
                    operation.description ||
                    "Live Mode operation",
                description: operation.description || "",
                requestFields: extractFields(appJsonSchema),
                responseCodes: Object.keys(operation.responses ?? {}).sort(),
                requestExample: extractRequestExample(operation),
            });
        });
    });

    return endpoints.sort((a, b) => a.path.localeCompare(b.path));
});

const endpointsForDocs = computed(() =>
    endpointsFromOpenApi.value.length > 0
        ? endpointsFromOpenApi.value
        : fallbackEndpoints
);

const ingestEndpoint = computed(
    () =>
        endpointsForDocs.value.find((endpoint) =>
            endpoint.path.includes("/ingest_event/")
        ) ?? endpointsForDocs.value[0]
);

const metricsEndpoint = computed(
    () =>
        endpointsForDocs.value.find((endpoint) =>
            endpoint.path.includes("/get_metrics/")
        ) ??
        endpointsForDocs.value[1] ??
        endpointsForDocs.value[0]
);

const ingestPath = computed(
    () => ingestEndpoint.value?.path ?? "/stek/live_mgt/ingest_event/start"
);

const metricsPath = computed(
    () => metricsEndpoint.value?.path ?? "/stek/live_mgt/get_metrics/start"
);

const fallbackExample = {
    stek_release: "v1.2.3",
    event_type: "order.created",
    process_instance_id: "run_abc123",
    business_key: "ORD-12345",
    process_id: "<process_uuid>",
    payload: {
        order_id: "ORD-12345",
        customer: "ACME Corp",
        total: 1500,
    },
};

const endpointExamplePayload = (endpoint: DocEndpoint) => {
    return endpoint.requestExample ?? fallbackExample;
};

const endpointExampleText = (endpoint: DocEndpoint) =>
    JSON.stringify(endpointExamplePayload(endpoint), null, 2);

const endpointCurlExample = (endpoint: DocEndpoint) =>
    `curl -X ${endpoint.method} "${apiBase}${endpoint.path}" \\\n  -H "Authorization: Bearer <api_key>" \\\n  -H "Organization: <organization_id>" \\\n  -H "Stek: <stek_id>" \\\n  -H "Content-Type: application/json" \\\n  -d '${endpointExampleText(endpoint)}'`;

const copyText = async (text: string, key: string) => {
    try {
        await navigator.clipboard.writeText(text);
        copiedKey.value = key;
        setTimeout(() => {
            if (copiedKey.value === key) {
                copiedKey.value = null;
            }
        }, 1200);
    } catch (error) {
        console.error("Failed to copy curl command", error);
    }
};

const loadOpenApi = async () => {
    isLoadingOpenApi.value = true;
    openApiError.value = "";

    try {
        const response = await fetch(openApiUrl, {
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error(`Failed to load OpenAPI: HTTP ${response.status}`);
        }

        openApiSpec.value = (await response.json()) as OpenApiSpec;
    } catch (error) {
        openApiSpec.value = null;
        openApiError.value =
            error instanceof Error ? error.message : "Unknown OpenAPI error";
    } finally {
        isLoadingOpenApi.value = false;
    }
};

onMounted(() => {
    void loadOpenApi();
});
</script>

<style lang="scss" scoped>
.icon-list {
    list-style: none;
    padding-left: 0;

    li {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
    }

    .pi {
        margin-top: 0.2rem;
        color: var(--stekz-primary-color);
        font-size: 0.9rem;
    }
}

.openapi-status {
    margin: 0;
    color: var(--text-color-secondary);

    &.error {
        color: var(--p-red-500, #d9534f);
    }
}

.endpoint-detail-card {
    border: 1px solid var(--stekz-outline-variant);
    border-radius: 0;
    padding: 1rem;
    background: linear-gradient(
        140deg,
        color-mix(in srgb, var(--stekz-primary-color), white 96%) 0%,
        color-mix(in srgb, var(--stekz-primary-color), white 98%) 100%
    );
    margin-bottom: 1rem;
    box-shadow: 0 1px 2px
        color-mix(in srgb, var(--stekz-primary-color), transparent 90%);
    transition:
        box-shadow 0.18s ease,
        transform 0.18s ease,
        border-color 0.18s ease;
}

.endpoint-detail-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px
        color-mix(in srgb, var(--stekz-primary-color), transparent 83%);
    border-color: color-mix(in srgb, var(--stekz-primary-color), white 35%);
}

.endpoint-detail-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.55rem;
}

.endpoint-method {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    border: 1px solid color-mix(in srgb, var(--stekz-primary-color), white 40%);
    color: var(--stekz-primary-color);
    background: color-mix(in srgb, var(--stekz-primary-color), white 90%);
    border-radius: 0;
    padding: 0.12rem 0.45rem;
    box-shadow: inset 0 0 0 1px
        color-mix(in srgb, var(--stekz-primary-color), white 45%);
}

.endpoint-path {
    font-size: 0.86rem;
}

.endpoint-description {
    margin: 0 0 0.75rem 0;
    color: var(--text-color-secondary);
}

.markdown-description :deep(p) {
    margin: 0 0 0.5rem;
}

.markdown-description :deep(p:last-child) {
    margin-bottom: 0;
}

.markdown-description :deep(ul),
.markdown-description :deep(ol) {
    margin: 0.35rem 0 0.6rem 1.1rem;
    padding: 0;
}

.markdown-description :deep(code) {
    padding: 0.02rem 0.3rem;
    background: color-mix(in srgb, var(--stekz-primary-color), white 93%);
    border: 1px solid var(--stekz-outline-variant);
}

.detail-sections {
    display: grid;
    grid-template-columns: 1fr 220px;
    gap: 0.75rem;
    align-items: start;
}

.detail-panel {
    border: 1px solid color-mix(in srgb, var(--stekz-primary-color), white 55%);
    background: color-mix(in srgb, var(--stekz-primary-color), white 97%);
    border-radius: 0;
    padding: 0.7rem 0.75rem;
}

.detail-panel-example {
    margin-top: 0.75rem;
}

.detail-panel-example pre {
    margin: 0.4rem 0 0;
}

.copy-block {
    position: relative;
}

.copy-button {
    position: absolute;
    top: 0.45rem;
    right: 0.45rem;
    border: 1px solid var(--stekz-outline-variant);
    background: color-mix(in srgb, var(--stekz-primary-color), white 94%);
    color: var(--text-color);
    border-radius: 0;
    padding: 0.2rem 0.45rem;
    font-size: 0.72rem;
    cursor: pointer;
}

.copy-block pre {
    padding-top: 1.8rem;
}

.detail-heading {
    font-weight: 600;
    margin: 0 0 0.3rem;
    color: var(--text-color);
}

.field-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        margin-bottom: 0.55rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px dashed
            color-mix(in srgb, var(--stekz-primary-color), white 60%);
    }

    li:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: 0;
    }
}

.field-topline {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.field-name {
    font-size: 0.82rem;
}

.field-type {
    font-size: 0.74rem;
    border: 1px solid var(--stekz-outline-variant);
    border-radius: 0;
    padding: 0.08rem 0.42rem;
    color: var(--text-color-secondary);
}

.field-required {
    font-size: 0.74rem;
    color: var(--stekz-primary-color);
    font-weight: 600;
    border: 1px solid color-mix(in srgb, var(--stekz-primary-color), white 45%);
    border-radius: 0;
    padding: 0.08rem 0.42rem;
    background: color-mix(in srgb, var(--stekz-primary-color), white 92%);
}

.field-desc {
    margin: 0.35rem 0 0;
    font-size: 0.87rem;
    color: var(--text-color-secondary);
}

.field-empty {
    color: var(--text-color-secondary);
    margin: 0;
}

.response-codes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.35rem;
}

.response-code {
    font-size: 0.76rem;
    border: 1px solid color-mix(in srgb, var(--stekz-primary-color), white 45%);
    border-radius: 0;
    padding: 0.12rem 0.46rem;
    background: color-mix(in srgb, var(--stekz-primary-color), white 92%);
    color: var(--stekz-primary-color);
}

@media screen and (max-width: 768px) {
    .detail-sections {
        grid-template-columns: 1fr;
    }
}
</style>
